import { RestError } from '@azure/core-http';
import { CourierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { naturalSort } from '../../../../core/util/natural-sort.util';
import { DropdownOption } from '../../../../ui/components/pure/dropdown/dropdown.component';
import { useQueryGetCarrierHierarchy } from '../../../carriers/carriers.queries';
import { useMutationCompleteCourierRegistration } from '../../couriers.queries';
import {
  CompleteRegistrationFormState,
  makeCompleteRegistrationFormConfig,
  mapCompleteRegistrationFormToDto
} from '../../logic/courier-form.map';

export type CompleteRegistrationActionState = {
  saving: boolean;
  error?: RestError | Error;
};

export interface CompleteRegistrationActionProps {
  /** A list row or the full record. */
  courier: Pick<CourierDto, 'id' | 'mobile' | 'project'>;
  onAbort(): void;
  onSuccess(): void;
}

export function useCompleteRegistrationAction({ courier, onAbort, onSuccess }: CompleteRegistrationActionProps) {
  const [state, setState] = useImmer<CompleteRegistrationActionState>({ saving: false });

  const { control, handleSubmit } = useForm<CompleteRegistrationFormState>({
    defaultValues: { groupID: '', firstName: '', lastName: '' }
  });

  // The courier's network, not the page's project: the endpoint doesn't check the carrier serves
  // that network, so the picker must.
  const hierarchy = useQueryGetCarrierHierarchy(courier.project?.id);

  const units = useMemo(() => hierarchy.data?.units ?? [], [hierarchy.data]);
  const unitMenuItems: DropdownOption<string>[] = useMemo(
    () => naturalSort(units.map((unit) => ({ label: unit.name, value: unit.id }))).asc((option) => option.label),
    [units]
  );

  const formConfig = makeCompleteRegistrationFormConfig();

  const completeRegistrationMutation = useMutationCompleteCourierRegistration(courier.id);

  const handlers = {
    onCancel: onAbort,
    onSubmit(formValues: CompleteRegistrationFormState) {
      setState((draft) => {
        draft.saving = true;
        draft.error = undefined;
      });

      completeRegistrationMutation.mutate(mapCompleteRegistrationFormToDto(formValues), {
        onSuccess() {
          onSuccess();
        },
        onError(error) {
          setState((draft) => {
            draft.saving = false;
            draft.error = castDraft(error);
          });
        }
      });
    }
  };

  return {
    state,
    handlers,
    control,
    handleSubmit,
    formConfig,
    unitMenuItems,
    loading: hierarchy.isLoading,
    hierarchyError: hierarchy.error as RestError | null
  } as const;
}
